import { type Address, maxUint256 } from 'viem'
import { useWriteContract } from 'wagmi'
import { Currency, CurrencyAmount, ROUTERS_MAP, RouterVersion, Trade, TradeType } from '@nominex/nomiswap-sdk'
import { useCallback, useMemo } from 'react'
import { useWagmi } from 'libs/wagmi'
import { Field } from 'state/swap/actions'
import { useHasPendingApproval, useTransactionAdder } from 'state/transactions/hooks'
import { computeSlippageAdjustedAmounts } from 'utils/prices'
import useTokenAllowance from './useTokenAllowance'
import bep20Abi from 'config/abi/IBep20Contract.json'

export enum ApprovalState {
  UNKNOWN,
  NOT_APPROVED,
  PENDING,
  APPROVED,
}

// returns a variable indicating the state of the approval and a function which approves if necessary or early returns
export function useApproveCallback(
  amountToApprove?: CurrencyAmount<Currency>,
  spender?: string,
): [ApprovalState, () => Promise<void>] {
  const { account, chainId } = useWagmi()
  const { writeContractAsync } = useWriteContract()
  const addTransaction = useTransactionAdder()

  const token = amountToApprove?.currency?.isToken ? amountToApprove.currency : undefined
  const currentAllowance = useTokenAllowance(token, account ?? undefined, spender)
  const pendingApproval = useHasPendingApproval(token?.address, spender)

  // check the current approval status
  const approvalState: ApprovalState = useMemo(() => {
    if (!amountToApprove || !spender) return ApprovalState.UNKNOWN
    if (amountToApprove.currency?.isNative) return ApprovalState.APPROVED
    // we might not have enough data to know whether or not we need to approve
    if (!currentAllowance) return ApprovalState.UNKNOWN

    // amountToApprove will be defined if currentAllowance is
    return currentAllowance.lessThan(amountToApprove)
      ? pendingApproval
        ? ApprovalState.PENDING
        : ApprovalState.NOT_APPROVED
      : ApprovalState.APPROVED
  }, [amountToApprove, currentAllowance, pendingApproval, spender])

  const approve = useCallback(async (): Promise<void> => {
    if (approvalState !== ApprovalState.NOT_APPROVED) {
      console.error('approve was called unnecessarily')
      return
    }
    if (!token) {
      console.error('no token')
      return
    }
    if (!amountToApprove) {
      console.error('missing amount to approve')
      return
    }
    if (!spender) {
      console.error('no spender')
      return
    }
    if (!chainId) {
      console.error('no chainId')
      return
    }

    try {
      let approvalAmount = maxUint256

      // Try max approval first, fallback to exact amount if it fails
      try {
        const hash = await writeContractAsync({
          address: token.address as Address,
          abi: bep20Abi as any,
          functionName: 'approve',
          args: [spender as Address, approvalAmount],
          chainId,
        } as any)

        const txResponse = { hash } as any
        addTransaction(txResponse, {
          summary: `Approve ${amountToApprove.currency.symbol}`,
          approval: { tokenAddress: token.address, spender },
        })
      } catch (error: any) {
        // If max approval fails, try exact amount (some tokens restrict approval amounts)
        console.warn('Max approval failed, trying exact amount', error)

        approvalAmount = BigInt(amountToApprove.quotient.toString())

        const hash = await writeContractAsync({
          address: token.address as Address,
          abi: bep20Abi as any,
          functionName: 'approve',
          args: [spender as Address, approvalAmount],
          chainId,
        } as any)

        const txResponse = { hash } as any
        addTransaction(txResponse, {
          summary: `Approve ${amountToApprove.currency.symbol}`,
          approval: { tokenAddress: token.address, spender },
        })
      }
    } catch (error: any) {
      console.error('Failed to approve token', error)
      throw error
    }
  }, [approvalState, token, amountToApprove, spender, chainId, writeContractAsync, addTransaction])

  return [approvalState, approve]
}

// wraps useApproveCallback in the context of a swap
export function useApproveCallbackFromTrade(trade: Trade<Currency, Currency, TradeType>, allowedSlippage, chainId) {
  const amountToApprove = useMemo(
    () => (trade ? computeSlippageAdjustedAmounts(trade, allowedSlippage || 0)[Field.INPUT] : undefined),
    [trade, allowedSlippage],
  )

  return useApproveCallback(amountToApprove, ROUTERS_MAP[chainId][RouterVersion.V3])
}
